import React, { useState } from 'react'
import { useNavigate }     from 'react-router-dom'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	IconButton,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useTheme,
} from '@mui/material'
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material'
import moment    from 'moment'
import LeftRight from '../LeftRight'
import { doGet, doPost } from '../../util/do-fetch'

const PROD_URL = 'https://hnbxxsxibdxeloeajxfe.supabase.co'

function ImportPrepKit() {
	const navigate = useNavigate()
	const theme    = useTheme()

	// Remote credentials
	const [remoteUrl,      setRemoteUrl]      = useState(PROD_URL)
	const [remoteKey,      setRemoteKey]      = useState('')
	const [showKey,        setShowKey]        = useState(false)
	const [userEmailOrId,  setUserEmailOrId]  = useState('')

	// Remote prepkits (loaded after user lookup)
	const [loadingPrepkits, setLoadingPrepkits] = useState(false)
	const [resolvedUser,    setResolvedUser]    = useState(null)   // { id, email } from remote
	const [remotePrepkits,  setRemotePrepkits]  = useState(null)   // null = not loaded yet
	const [selectedPrepkit, setSelectedPrepkit] = useState(null)

	// Local (dev) users
	const [loadingUsers, setLoadingUsers] = useState(false)
	const [localUsers,   setLocalUsers]   = useState(null)         // null = not loaded yet
	const [selectedUser, setSelectedUser] = useState(null)

	// Import state
	const [importing, setImporting] = useState(false)
	const [result,    setResult]    = useState(null)
	const [error,     setError]     = useState(null)

	// ── Load remote prepkits ──────────────────────────────────────────────────
	const handleLoadPrepkits = async () => {
		setLoadingPrepkits(true)
		setResolvedUser(null)
		setRemotePrepkits(null)
		setSelectedPrepkit(null)
		setResult(null)
		setError(null)
		try {
			const response = await doPost('/admin/prepkit-import/list', {
				body: JSON.stringify({ remoteUrl, remoteKey, userEmailOrId }),
			})
			if (!response.ok) {
				const msg = await response.text()
				throw new Error(msg)
			}
			const data = await response.json()
			setResolvedUser(data.user)
			setRemotePrepkits(data.prepkits)
		} catch (err) {
			setError(`Failed to load prepkits: ${err.message}`)
		} finally {
			setLoadingPrepkits(false)
		}
	}

	// ── Load local users ──────────────────────────────────────────────────────
	const handleLoadUsers = async () => {
		setLoadingUsers(true)
		setLocalUsers(null)
		setSelectedUser(null)
		try {
			const response = await doGet('/admin/users?page=1&limit=100&s=&sortBy=created_at&sortDir=desc')
			const data     = await response.json()
			setLocalUsers(data.users || [])
		} catch (err) {
			setError(`Failed to load dev users: ${err.message}`)
		} finally {
			setLoadingUsers(false)
		}
	}

	// ── Run import ────────────────────────────────────────────────────────────
	const handleImport = async () => {
		setImporting(true)
		setResult(null)
		setError(null)
		try {
			const response = await doPost('/admin/prepkit-import/run', {
				body: JSON.stringify({
					remoteUrl,
					remoteKey,
					prepkitId:    selectedPrepkit.id,
					targetUserId: selectedUser.user_id,
				}),
			})
			if (!response.ok) {
				const msg = await response.text()
				throw new Error(msg)
			}
			const data = await response.json()
			setResult(data)
		} catch (err) {
			setError(`Import failed: ${err.message}`)
		} finally {
			setImporting(false)
		}
	}

	const canLoad   = remoteUrl && remoteKey && userEmailOrId && !loadingPrepkits
	const canImport = selectedPrepkit && selectedUser && !importing && !result

	return (
		<Container maxWidth="lg">
			<Box mt={3} mb={6}>

				{/* Header */}
				<LeftRight
					left={
						<Box display="flex" alignItems="center">
							<IconButton onClick={() => navigate('/admin')}>
								<ArrowBack />
							</IconButton>
							<Typography variant="h5">Import PrepKit</Typography>
						</Box>
					}
				/>
				<Divider sx={{ my: 2 }} />

				{/* ── Remote credentials ─────────────────────────────────────────── */}
				<Typography variant="h6" mb={1}>Remote Database</Typography>
				<Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
					<TextField
						label="Remote Supabase URL"
						value={remoteUrl}
						onChange={e => setRemoteUrl(e.target.value)}
						size="small"
						sx={{ minWidth: 340 }}
					/>
					<TextField
						label="Service Role Key"
						value={remoteKey}
						onChange={e => setRemoteKey(e.target.value)}
						type={showKey ? 'text' : 'password'}
						size="small"
						sx={{ minWidth: 340 }}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton size="small" onClick={() => setShowKey(v => !v)}>
										{showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
					<TextField
						label="Remote User Email or ID"
						value={userEmailOrId}
						onChange={e => setUserEmailOrId(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && canLoad && handleLoadPrepkits()}
						size="small"
						sx={{ minWidth: 260 }}
						placeholder="user@example.com or UUID"
					/>
					<Button
						variant="contained"
						onClick={handleLoadPrepkits}
						disabled={!canLoad}
					>
						{loadingPrepkits ? <CircularProgress size={18} color="inherit" /> : 'Load PrepKits'}
					</Button>
				</Box>

				{/* ── Remote prepkit list ────────────────────────────────────────── */}
				{remotePrepkits !== null && (
					<Box mt={3}>
						<Typography variant="h6" mb={1}>
							Select PrepKit
							{resolvedUser && (
								<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
									— {remotePrepkits.length} prepkit(s) for {resolvedUser.email}
								</Typography>
							)}
							{selectedPrepkit && (
								<Chip
									label={selectedPrepkit.name}
									onDelete={() => setSelectedPrepkit(null)}
									size="small"
									color="primary"
									sx={{ ml: 1 }}
								/>
							)}
						</Typography>
						<TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Package</TableCell>
										<TableCell align="center">Trial</TableCell>
										<TableCell align="right">Created</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{remotePrepkits.map(pk => (
										<TableRow
											key={pk.id}
											hover
											selected={selectedPrepkit?.id === pk.id}
											onClick={() => setSelectedPrepkit(pk)}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell>
												<Typography variant="body2" fontWeight={selectedPrepkit?.id === pk.id ? 'bold' : 'normal'}>
													{pk.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">{pk.id}</Typography>
											</TableCell>
											<TableCell>{pk.package_name || '—'}</TableCell>
											<TableCell align="center">{pk.is_trial ? 'yes' : 'no'}</TableCell>
											<TableCell align="right">
												{pk.created_at ? moment(pk.created_at).format('MMM D, YYYY') : '—'}
											</TableCell>
										</TableRow>
									))}
									{remotePrepkits.length === 0 && (
										<TableRow>
											<TableCell colSpan={4}>
												<Typography color="text.secondary" variant="body2">No prepkits found for this user.</Typography>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{/* ── Dev user list ──────────────────────────────────────────────── */}
				<Box mt={3}>
					<Box display="flex" alignItems="center" gap={2} mb={1}>
						<Typography variant="h6">
							Select Target User
							{selectedUser && (
								<Chip
									label={selectedUser.email}
									onDelete={() => setSelectedUser(null)}
									size="small"
									color="secondary"
									sx={{ ml: 1 }}
								/>
							)}
						</Typography>
						<Button
							variant="outlined"
							size="small"
							onClick={handleLoadUsers}
							disabled={loadingUsers}
						>
							{loadingUsers ? <CircularProgress size={14} color="inherit" /> : 'Load Dev Users'}
						</Button>
					</Box>

					{localUsers !== null && (
						<TableContainer sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Email</TableCell>
										<TableCell>Name</TableCell>
										<TableCell align="right">Created</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{localUsers.map(u => (
										<TableRow
											key={u.user_id}
											hover
											selected={selectedUser?.user_id === u.user_id}
											onClick={() => setSelectedUser(u)}
											sx={{ cursor: 'pointer' }}
										>
											<TableCell>
												<Typography variant="body2" fontWeight={selectedUser?.user_id === u.user_id ? 'bold' : 'normal'}>
													{u.email}
												</Typography>
												<Typography variant="caption" color="text.secondary">{u.user_id}</Typography>
											</TableCell>
											<TableCell>{u.first_name} {u.last_name}</TableCell>
											<TableCell align="right">
												{u.created_at ? moment(u.created_at).format('MMM D, YYYY') : '—'}
											</TableCell>
										</TableRow>
									))}
									{localUsers.length === 0 && (
										<TableRow>
											<TableCell colSpan={3}>
												<Typography color="text.secondary" variant="body2">No users found.</Typography>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>

				{/* ── Import action ──────────────────────────────────────────────── */}
				<Divider sx={{ my: 3 }} />
				<Box display="flex" alignItems="center" gap={2}>
					<Button
						variant="contained"
						color="primary"
						onClick={handleImport}
						disabled={!canImport}
						size="large"
					>
						{importing
							? <><CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />Importing…</>
							: 'Import PrepKit'
						}
					</Button>
					{!selectedPrepkit && remotePrepkits !== null && (
						<Typography variant="body2" color="text.secondary">Select a prepkit above.</Typography>
					)}
					{selectedPrepkit && !selectedUser && (
						<Typography variant="body2" color="text.secondary">Select a target user above.</Typography>
					)}
				</Box>

				{/* ── Error ─────────────────────────────────────────────────────── */}
				{error && (
					<Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
						{error}
					</Alert>
				)}

				{/* ── Success ───────────────────────────────────────────────────── */}
				{result && (
					<Alert severity="success" sx={{ mt: 2 }}>
						<Typography fontWeight="bold">Import complete: {result.prepkitName}</Typography>
						<Typography variant="body2">
							{result.counts.rounds} rounds · {result.counts.answers} answers ·{' '}
							{result.counts.messages} messages · {result.counts.licenses} license(s)
						</Typography>
						<Box mt={1}>
							<Button
								size="small"
								variant="outlined"
								color="success"
								onClick={() => navigate(`/prepkit/${result.prepkitId}`)}
							>
								Open PrepKit
							</Button>
						</Box>
					</Alert>
				)}

			</Box>
		</Container>
	)
}

export default ImportPrepKit
